import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const gradientShapes = shapes.wave;

// ---------------------------------------------------------------------------
// Gradera brand palette — sourced from the logo gradient
// ---------------------------------------------------------------------------
const brand = {
  // Cyan — primary brand colour
  cyan: { main: '#0891b2', light: '#22d3ee', dark: '#0e7490' },
  cyanLight: { main: '#22d3ee', light: '#67e8f9', dark: '#0891b2' },
  // Purple — secondary brand colour (gradient start of the logo mark)
  purple: { main: '#66509B', light: '#9478b9', dark: '#4a3771' },
  purpleLight: { main: '#9478b9', light: '#c4b0d8', dark: '#66509B' },
};

// ---------------------------------------------------------------------------
// Page-header gradient pairs per entity type.
// Each pair reflects a distinct stop from the Gradera brand gradient so that
// different entity types are visually distinguishable at a glance.
// ---------------------------------------------------------------------------
const pageColorPairs: Record<string, [string, string]> = {
  home: ['#0891b2', '#0e7490'],
  documentation: ['#2D86C5', '#0891b2'],
  tool: ['#66509B', '#2D86C5'],
  service: ['#0CA6DF', '#0891b2'],
  website: ['#2FB3CB', '#0e7490'],
  library: ['#57C2B6', '#2FB3CB'],
  other: ['#66509B', '#0891b2'],
  app: ['#0CA6DF', '#2D86C5'],
  apis: ['#26C3F3', '#0CA6DF'],
};

/**
 * Build pageTheme entries from `pageColorPairs`.
 * When `brighten` is true the darker stops are swapped for lighter equivalents
 * so the gradients remain visible against the near-black dark-mode background.
 */
const buildPageTheme = (brighten = false) =>
  Object.fromEntries(
    Object.entries(pageColorPairs).map(([key, [from, to]]) => [
      key,
      genPageTheme({
        colors: brighten
          ? ([
              from.replace('#0891b2', '#22d3ee').replace('#0e7490', '#0ea5e9'),
              to,
            ] as [string, string])
          : [from, to],
        shape: gradientShapes,
      }),
    ]),
  );

// ---------------------------------------------------------------------------
// Shared MUI component overrides
// ---------------------------------------------------------------------------
const sharedComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '9999px',
        textTransform: 'none' as const,
        fontWeight: 500,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: '8px',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '9999px',
      },
    },
  },
};

// ===========================================================================
// Light theme
// ===========================================================================
export const graderaLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: brand.cyan,
    // Secondary uses Gradera brand purple — distinct from the cyan primary so
    // that chips, badges, and focus indicators have their own visual identity.
    secondary: brand.purple,
    navigation: {
      background: '#18181b',
      indicator: '#0891b2',
      color: '#a1a1aa',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: '#27272a',
      },
      submenu: {
        background: '#27272a',
      },
    },
    background: {
      default: '#f4f4f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#27272a',
      secondary: '#52525b',
      disabled: '#71717a',
    },
    divider: '#e4e4e7',
    error: { main: '#dc2626' },
    warning: { main: '#d97706' },
    success: { main: '#059669' },
    info: { main: '#0891b2' },
  },
  defaultPageTheme: 'home',
  fontFamily: '"Inter", "Geist", sans-serif',
  components: {
    ...sharedComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
  },
  pageTheme: buildPageTheme(),
});

// ===========================================================================
// Dark theme
// ===========================================================================
export const graderaDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: brand.cyanLight,
    // Secondary uses a lighter Gradera purple so it remains accessible against
    // the dark background while still being visually distinct from the primary.
    secondary: brand.purpleLight,
    navigation: {
      background: '#09090b',
      indicator: '#22d3ee',
      color: '#a1a1aa',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: '#18181b',
      },
      submenu: {
        background: '#18181b',
      },
    },
    background: {
      default: '#09090b',
      paper: '#18181b',
    },
    text: {
      primary: '#fafafa',
      secondary: '#a1a1aa',
      disabled: '#71717a',
    },
    divider: '#27272a',
    error: { main: '#f87171' },
    warning: { main: '#fbbf24' },
    success: { main: '#34d399' },
    info: { main: '#22d3ee' },
  },
  defaultPageTheme: 'home',
  fontFamily: '"Inter", "Geist", sans-serif',
  components: {
    ...sharedComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        },
      },
    },
  },
  // Brighten the leading gradient stop so page headers remain vibrant on the
  // near-black (#09090b) background.
  pageTheme: buildPageTheme(true),
});
